/**
 * Enhanced Performance Monitoring Service
 *
 * Provides comprehensive performance monitoring, optimization tracking,
 * and real-time performance insights for the frontend application
 */

import {
  trackPageLoad,
  getLCP,
  getFID,
  getCLS,
  getTTFB,
  useComponentPerformance,
} from "../utils/performanceMonitor";

class PerformanceService {
  constructor() {
    this.isMonitoring = false;
    this.lifecycleVersion = 0;
    this.metrics = {
      pageLoads: [],
      userInteractions: [],
      apiCalls: [],
      errors: [],
      resourceTimings: [],
      routeMetrics: new Map(),
      bundleMetrics: new Map(),
      componentMetrics: new Map(),
      memoryMetrics: [],
      performanceScores: [],
    };
    // Flat, newest-first log of recordMetric() samples; the stats read this.
    this.samples = [];
    this.lastMetricTimestamps = {};
    this.observers = new Set();
    this.eventListeners = [];
    this.timeouts = new Set();
    this.intervals = new Set();
    this.historyWrappers = null;
    this.config = {
      enableResourceTiming: true,
      enableUserTiming: true,
      enableLongTaskMonitoring: true,
      enableMemoryMonitoring: true,
      enableBundleSizeTracking: true,
      sampleRate: 1.0,
    };
    this.debounceInterval = 15000;
    this.thresholds = {
      pageLoad: 3000,
      apiCall: 2000,
      render: 100,
      interaction: 50,
      lcp: 2500,
      fid: 100,
      cls: 0.1,
      ttfb: 600,
      longTask: 50,
      bundleSize: 500 * 1024,
    };
    this.routeStartTime = null;
    this.currentRoute = null;
  }

  /**
   * Initialize performance monitoring
   */
  init(config = {}) {
    if (this.isMonitoring) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    this.config = { ...this.config, ...config };
    this.lifecycleVersion += 1;
    this.isMonitoring = true;

    try {
      this.setupPerformanceObservers();
      this.monitorPageLoad();

      if (this.config.enableMemoryMonitoring) {
        this.monitorMemoryUsage();
      }
      if (this.config.enableResourceTiming) {
        this.monitorResourceTiming();
      }
      if (this.config.enableLongTaskMonitoring) {
        this.monitorLongTasks();
      }
      if (this.config.enableBundleSizeTracking) {
        this.setupBundleTracking();
      }

      this.setupErrorTracking();
      this.setupRouteTracking();
      this.setupEnhancedMonitoring();

      if (import.meta.env.MODE === "development") {
        this.createDeveloperTools();
      }
    } catch (error) {
      this.cleanup();
      throw error;
    }
  }

  isLifecycleActive(lifecycleVersion) {
    return (
      this.isMonitoring && this.lifecycleVersion === lifecycleVersion
    );
  }

  addObserver(observer) {
    this.observers.add(observer);
    return observer;
  }

  addOwnedEventListener(target, type, listener, options) {
    target.addEventListener(type, listener, options);
    this.eventListeners.push({ target, type, listener, options });
  }

  scheduleTimeout(callback, delay) {
    const lifecycleVersion = this.lifecycleVersion;
    const timeoutId = setTimeout(() => {
      this.timeouts.delete(timeoutId);
      if (this.isLifecycleActive(lifecycleVersion)) {
        callback();
      }
    }, delay);

    this.timeouts.add(timeoutId);
    return timeoutId;
  }

  scheduleInterval(callback, delay) {
    const lifecycleVersion = this.lifecycleVersion;
    const intervalId = setInterval(() => {
      if (this.isLifecycleActive(lifecycleVersion)) {
        callback();
      }
    }, delay);

    this.intervals.add(intervalId);
    return intervalId;
  }

  /**
   * Setup performance observers for various metrics
   */
  setupPerformanceObservers() {
    if (typeof window.PerformanceObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;

    try {
      const lcpObserver = new window.PerformanceObserver((list) => {
        if (!this.isLifecycleActive(lifecycleVersion)) return;
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (!lastEntry) return;

        this.recordMetric("lcp", lastEntry.startTime, {
          element: lastEntry.element?.tagName || "unknown",
        });
      });
      lcpObserver.observe({ entryTypes: ["largest-contentful-paint"] });
      this.addObserver(lcpObserver);
    } catch (error) {
      // Unsupported performance entry type.
    }

    try {
      const fidObserver = new window.PerformanceObserver((list) => {
        if (!this.isLifecycleActive(lifecycleVersion)) return;
        list.getEntries().forEach((entry) => {
          this.recordMetric("fid", entry.processingStart - entry.startTime, {
            inputType: entry.name,
          });
        });
      });
      fidObserver.observe({ entryTypes: ["first-input"] });
      this.addObserver(fidObserver);
    } catch (error) {
      // Unsupported performance entry type.
    }

    try {
      const clsObserver = new window.PerformanceObserver((list) => {
        if (!this.isLifecycleActive(lifecycleVersion)) return;
        let clsValue = 0;
        list.getEntries().forEach((entry) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        if (clsValue > 0) {
          this.recordMetric("cls", clsValue);
        }
      });
      clsObserver.observe({ entryTypes: ["layout-shift"] });
      this.addObserver(clsObserver);
    } catch (error) {
      // Unsupported performance entry type.
    }
  }

  /**
   * Monitor page load performance
   */
  monitorPageLoad() {
    if (!("performance" in window) || !performance.getEntriesByType) return;

    const lifecycleVersion = this.lifecycleVersion;
    const handleLoad = () => {
      if (!this.isLifecycleActive(lifecycleVersion)) return;

      this.scheduleTimeout(() => {
        const navigation = performance.getEntriesByType("navigation")[0];
        if (!navigation) return;

        this.recordMetric(
          "pageLoad",
          navigation.loadEventEnd - navigation.fetchStart,
          {
            domContentLoaded:
              navigation.domContentLoadedEventEnd - navigation.fetchStart,
            domInteractive: navigation.domInteractive - navigation.fetchStart,
            transferSize: navigation.transferSize,
          },
        );
      }, 0);
    };

    this.addOwnedEventListener(window, "load", handleLoad);
  }

  /**
   * Monitor memory usage
   */
  monitorMemoryUsage() {
    if (!("memory" in performance)) return;

    this.scheduleInterval(() => {
      const memory = performance.memory;
      this.recordMetric("memory", memory.usedJSHeapSize / 1048576, {
        total: memory.totalJSHeapSize / 1048576,
        limit: memory.jsHeapSizeLimit / 1048576,
      });
    }, 300000);
  }

  /**
   * Record a performance metric
   */
  recordMetric(type, value, context = {}) {
    const metric = {
      id: this.generateMetricId(),
      type,
      value,
      timestamp: Date.now(),
      url: window.location.pathname,
      context,
      isSlowPerformance: this.isSlowPerformance(type, value),
    };

    // Debounce metric recording
    const now = Date.now();
    if (
      this.lastMetricTimestamps[type] &&
      now - this.lastMetricTimestamps[type] < this.debounceInterval
    ) {
      return;
    }
    this.lastMetricTimestamps[type] = now;

    this.samples.unshift(metric);
    // Keep only recent samples (last 1000)
    if (this.samples.length > 1000) {
      this.samples.length = 1000;
    }

    // Log slow performance in development
    if (!this.isProduction && metric.isSlowPerformance) {
      console.debug(`Slow ${type}: ${value.toFixed(2)}ms`, context);
    }

    // Alert on critical performance issues
    if (metric.isSlowPerformance && this.isCriticalThreshold(type, value)) {
      this.alertSlowPerformance(metric);
    }
  }

  /**
   * Generate unique metric ID
   */
  generateMetricId() {
    return `metric_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  }

  /**
   * Check if performance is slow based on thresholds
   */
  isSlowPerformance(type, value) {
    return value > (this.thresholds[type] || 1000);
  }

  /**
   * Check if performance exceeds critical threshold
   */
  isCriticalThreshold(type, value) {
    const criticalMultiplier = 2;
    return value > this.thresholds[type] * criticalMultiplier;
  }

  /**
   * Alert on slow performance
   */
  alertSlowPerformance(metric) {
    if (this.isProduction) {
      // In production, send to monitoring service
      console.error("Critical performance issue:", metric);
    } else {
      // In development, show detailed warning
      console.debug(
        `Critical ${metric.type}: ${metric.value.toFixed(2)}ms (threshold ${this.thresholds[metric.type]}ms)`,
        metric.url,
      );
    }
  }

  /**
   * Time an API call
   */
  timeApiCall(url, method = "GET") {
    const startTime = performance.now();

    return {
      end: (success = true, responseSize = 0) => {
        const duration = performance.now() - startTime;
        this.recordMetric("apiCall", duration, {
          url,
          method,
          success,
          responseSize,
        });
        return duration;
      },
    };
  }

  /**
   * Time a component render
   */
  timeRender(componentName) {
    const startTime = performance.now();

    return {
      end: () => {
        const duration = performance.now() - startTime;
        this.recordMetric("render", duration, {
          component: componentName,
        });
        return duration;
      },
    };
  }

  /**
   * Time a user interaction
   */
  timeInteraction(interactionType, target) {
    const startTime = performance.now();

    return {
      end: () => {
        const duration = performance.now() - startTime;
        this.recordMetric("interaction", duration, {
          type: interactionType,
          target: target?.tagName || "unknown",
        });
        return duration;
      },
    };
  }

  /**
   * Get performance statistics
   */
  getPerformanceStats() {
    const now = Date.now();
    const last24Hours = now - 24 * 60 * 60 * 1000;
    const recentMetrics = this.samples.filter((m) => m.timestamp > last24Hours);

    const statsByType = recentMetrics.reduce((acc, metric) => {
      if (!acc[metric.type]) {
        acc[metric.type] = [];
      }
      acc[metric.type].push(metric.value);
      return acc;
    }, {});

    const stats = {};
    Object.entries(statsByType).forEach(([type, values]) => {
      const sorted = values.sort((a, b) => a - b);
      stats[type] = {
        count: values.length,
        min: Math.min(...values),
        max: Math.max(...values),
        avg: values.reduce((sum, val) => sum + val, 0) / values.length,
        median: sorted[Math.floor(sorted.length / 2)],
        p95: sorted[Math.floor(sorted.length * 0.95)],
        slowCount: values.filter((v) => this.isSlowPerformance(type, v)).length,
      };
    });

    return {
      totalMetrics: this.samples.length,
      recentMetrics: recentMetrics.length,
      stats,
      slowPages: this.getSlowPages(),
      memoryUsage: this.getCurrentMemoryUsage(),
    };
  }

  /**
   * Get pages with slow performance
   */
  getSlowPages() {
    const pageMetrics = this.samples
      .filter((m) => m.type === "pageLoad" || m.type === "render")
      .reduce((acc, metric) => {
        if (!acc[metric.url]) {
          acc[metric.url] = [];
        }
        acc[metric.url].push(metric.value);
        return acc;
      }, {});

    return Object.entries(pageMetrics)
      .map(([url, values]) => ({
        url,
        avgTime: values.reduce((sum, val) => sum + val, 0) / values.length,
        maxTime: Math.max(...values),
        loadCount: values.length,
      }))
      .filter((page) => page.avgTime > this.thresholds.pageLoad)
      .sort((a, b) => b.avgTime - a.avgTime)
      .slice(0, 10);
  }

  /**
   * Get current memory usage
   */
  getCurrentMemoryUsage() {
    if ("memory" in performance) {
      const memory = performance.memory;
      return {
        used: (memory.usedJSHeapSize / 1048576).toFixed(2) + " MB",
        total: (memory.totalJSHeapSize / 1048576).toFixed(2) + " MB",
        limit: (memory.jsHeapSizeLimit / 1048576).toFixed(2) + " MB",
        usage:
          ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(1) +
          "%",
      };
    }
    return null;
  }

  /**
   * Monitor resource timing
   */
  monitorResourceTiming() {
    if (typeof window.PerformanceObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;
    try {
      const resourceObserver = new window.PerformanceObserver((list) => {
        if (!this.isLifecycleActive(lifecycleVersion)) return;
        for (const entry of list.getEntries()) {
          this.handleResourceTiming(entry);
        }
      });
      resourceObserver.observe({ entryTypes: ["resource"] });
      this.addObserver(resourceObserver);
    } catch (error) {
      console.warn("Resource timing observer not supported");
    }
  }

  /**
   * Monitor long tasks
   */
  monitorLongTasks() {
    if (typeof window.PerformanceObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;
    try {
      const longTaskObserver = new window.PerformanceObserver((list) => {
        if (!this.isLifecycleActive(lifecycleVersion)) return;
        for (const entry of list.getEntries()) {
          this.handleLongTask(entry);
        }
      });
      longTaskObserver.observe({ entryTypes: ["longtask"] });
      this.addObserver(longTaskObserver);
    } catch (error) {
      console.warn("Long task observer not supported");
    }
  }

  /**
   * Set up error tracking
   */
  setupErrorTracking() {
    const lifecycleVersion = this.lifecycleVersion;
    const handleError = (event) => {
      if (!this.isLifecycleActive(lifecycleVersion)) return;
      this.trackError({
        type: "javascript",
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
        timestamp: new Date(),
      });
    };
    const handleUnhandledRejection = (event) => {
      if (!this.isLifecycleActive(lifecycleVersion)) return;
      this.trackError({
        type: "promise",
        message: event.reason?.message || "Unhandled promise rejection",
        stack: event.reason?.stack,
        timestamp: new Date(),
      });
    };

    this.addOwnedEventListener(window, "error", handleError);
    this.addOwnedEventListener(
      window,
      "unhandledrejection",
      handleUnhandledRejection,
    );
  }

  /**
   * Handle resource timing
   */
  handleResourceTiming(entry) {
    // Only track significant resources
    if (entry.duration < 10) return;

    const resourceMetrics = {
      timestamp: new Date(),
      name: entry.name,
      type: this.getResourceType(entry.name),
      duration: entry.duration,
      size: entry.transferSize || 0,
      cached: entry.transferSize === 0 && entry.decodedBodySize > 0,
    };

    this.metrics.resourceTimings.push(resourceMetrics);

    // Alert for slow resources
    if (entry.duration > 1000) {
      console.warn("Slow resource detected:", resourceMetrics);
      this.sendMetricsToBackend("slowResource", resourceMetrics);
    }
  }

  /**
   * Handle long tasks
   */
  handleLongTask(entry) {
    const longTaskMetrics = {
      timestamp: new Date(),
      duration: entry.duration,
      startTime: entry.startTime,
    };

    // Long tasks are routine in development; they go to the backend, and the
    // console stays readable for real errors.
    this.sendMetricsToBackend("longTask", longTaskMetrics);
  }

  /**
   * Track user interaction
   */
  trackInteraction(type, target, metadata = {}) {
    const targetId = typeof target === "string" ? target : target?.id || null;
    const interaction = {
      timestamp: new Date(),
      type,
      target:
        typeof target === "string" ? target : target?.tagName || "unknown",
      targetId,
      targetClass:
        typeof target === "string" ? null : target?.className || null,
      url: window.location.href,
      metadata,
    };

    this.metrics.userInteractions.push(interaction);
    if (this.metrics.userInteractions.length > 100) {
      this.metrics.userInteractions.shift();
    }
  }

  /**
   * Track API call performance
   */
  trackApiCall(url, method, duration, status, error = null) {
    const apiCall = {
      timestamp: new Date(),
      url,
      method,
      duration,
      status,
      error: error?.message || null,
      success: status >= 200 && status < 400,
    };

    this.metrics.apiCalls.push(apiCall);

    // Keep only recent API calls
    if (this.metrics.apiCalls.length > 100) {
      this.metrics.apiCalls.shift();
    }

    // Alert for slow API calls
    if (duration > 2000) {
      console.warn("Slow API call detected:", apiCall);
      this.sendMetricsToBackend("slowApiCall", apiCall);
    }
  }

  /**
   * Track error
   */
  trackError(error) {
    this.metrics.errors.push(error);

    // Keep only recent errors
    if (this.metrics.errors.length > 50) {
      this.metrics.errors.shift();
    }

    this.sendMetricsToBackend("error", error);
  }

  /**
   * Get resource type from URL
   */
  getResourceType(url) {
    if (url.includes(".js")) return "script";
    if (url.includes(".css")) return "stylesheet";
    if (url.match(/\.(png|jpg|jpeg|gif|svg|webp)$/)) return "image";
    if (url.includes("/api/")) return "api";
    return "other";
  }

  /**
   * Send metrics to backend
   */
  async sendMetricsToBackend(type, data) {
    // Sample the data based on sample rate
    if (Math.random() > this.config.sampleRate) {
      return;
    }

    try {
      // this request has been running non stop preventing other requests so temporarily commenting it out
      // await fetch(`${API_BASE_URL}/performance/frontend`, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify({
      //     type,
      //     data,
      //     userAgent: navigator.userAgent,
      //     url: window.location.href,
      //     timestamp: new Date().toISOString(),
      //   }),
      // });
    } catch (error) {
      // Silently fail to avoid affecting user experience
      console.debug("Failed to send performance metrics:", error);
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary() {
    return {
      pageLoads: this.metrics.pageLoads?.length || 0,
      userInteractions: this.metrics.userInteractions?.length || 0,
      apiCalls: this.metrics.apiCalls?.length || 0,
      errors: this.metrics.errors?.length || 0,
      resourceTimings: this.metrics.resourceTimings?.length || 0,
      recentErrors: this.metrics.errors?.slice(-5) || [],
      slowApiCalls:
        this.metrics.apiCalls?.filter((call) => call.duration > 1000) || [],
      memoryUsage:
        "memory" in performance
          ? {
              used: performance.memory.usedJSHeapSize,
              total: performance.memory.totalJSHeapSize,
              percentage:
                (performance.memory.usedJSHeapSize /
                  performance.memory.totalJSHeapSize) *
                100,
            }
          : null,
    };
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.samples = [];
    this.metrics = {
      pageLoads: [],
      userInteractions: [],
      apiCalls: [],
      errors: [],
      resourceTimings: [],
    };
  }

  /**
   * Get all metrics
   */
  getAllMetrics() {
    return { ...this.metrics };
  }

  /**
   * Cleanup and stop monitoring
   */
  cleanup() {
    this.isMonitoring = false;
    this.lifecycleVersion += 1;

    this.observers.forEach((observer) => observer.disconnect());
    this.observers.clear();

    this.eventListeners.forEach(
      ({ target, type, listener, options }) =>
        target.removeEventListener(type, listener, options),
    );
    this.eventListeners = [];

    this.timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeouts.clear();
    this.intervals.forEach((intervalId) => clearInterval(intervalId));
    this.intervals.clear();

    if (this.historyWrappers) {
      const { history, pushState, replaceState } = this.historyWrappers;
      if (history.pushState === pushState.wrapper) {
        history.pushState = pushState.original;
      }
      if (history.replaceState === replaceState.wrapper) {
        history.replaceState = replaceState.original;
      }
      this.historyWrappers = null;
    }

    this.currentRoute = null;
    this.routeStartTime = null;
  }

  /**
   * Set up enhanced performance monitoring
   */
  setupEnhancedMonitoring() {
    if (typeof window.PerformanceObserver !== "function") return;

    const lifecycleVersion = this.lifecycleVersion;
    try {
      const enhancedResourceObserver = new window.PerformanceObserver(
        (entries) => {
          if (!this.isLifecycleActive(lifecycleVersion)) return;

          entries.getEntries().forEach((entry) => {
            if (
              entry.initiatorType === "script" &&
              entry.name.includes("/assets/js/")
            ) {
              this.trackBundleLoad(entry);
            }
            this.handleResourceTiming(entry);
          });
        },
      );
      enhancedResourceObserver.observe({ entryTypes: ["resource"] });
      this.addObserver(enhancedResourceObserver);
    } catch (error) {
      console.warn("Enhanced resource observer setup failed:", error);
    }
  }

  /**
   * Set up route performance tracking
   */
  setupRouteTracking() {
    const lifecycleVersion = this.lifecycleVersion;
    const history = window.history;
    const service = this;
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    const pushStateWrapper = function (...args) {
      const result = Reflect.apply(originalPushState, this, args);
      if (service.isLifecycleActive(lifecycleVersion)) {
        service.handleRouteChange();
      }
      return result;
    };
    const replaceStateWrapper = function (...args) {
      const result = Reflect.apply(originalReplaceState, this, args);
      if (service.isLifecycleActive(lifecycleVersion)) {
        service.handleRouteChange();
      }
      return result;
    };

    history.pushState = pushStateWrapper;
    history.replaceState = replaceStateWrapper;
    this.historyWrappers = {
      history,
      pushState: {
        original: originalPushState,
        wrapper: pushStateWrapper,
      },
      replaceState: {
        original: originalReplaceState,
        wrapper: replaceStateWrapper,
      },
    };

    const handlePopState = () => {
      if (this.isLifecycleActive(lifecycleVersion)) {
        this.handleRouteChange();
      }
    };
    this.addOwnedEventListener(window, "popstate", handlePopState);
    this.handleRouteChange();
  }

  /**
   * Handle route changes for performance tracking
   */
  handleRouteChange() {
    if (!this.isMonitoring) return;

    const newRoute = window.location.pathname;
    if (this.currentRoute && this.routeStartTime !== null) {
      const duration = performance.now() - this.routeStartTime;
      this.recordRouteMetrics(this.currentRoute, duration);
    }

    this.currentRoute = newRoute;
    this.routeStartTime = performance.now();
    trackPageLoad(newRoute);
    this.scheduleTimeout(() => this.collectWebVitals(newRoute), 3000);
  }

  /**
   * Record route performance metrics
   */
  recordRouteMetrics(route, duration) {
    const metrics = this.metrics.routeMetrics.get(route) || {
      visits: 0,
      totalDuration: 0,
      avgDuration: 0,
      webVitals: [],
    };

    metrics.visits++;
    metrics.totalDuration += duration;
    metrics.avgDuration = metrics.totalDuration / metrics.visits;

    this.metrics.routeMetrics.set(route, metrics);
  }

  /**
   * Collect Web Vitals for a route
   */
  collectWebVitals(route) {
    const vitals = {
      lcp: getLCP(),
      fid: getFID(),
      cls: getCLS(),
      ttfb: getTTFB(),
      timestamp: Date.now(),
    };

    const metrics = this.metrics.routeMetrics.get(route) || {
      visits: 0,
      totalDuration: 0,
      avgDuration: 0,
      webVitals: [],
    };

    metrics.webVitals.push(vitals);
    this.metrics.routeMetrics.set(route, metrics);

    // Check against thresholds
    this.checkPerformanceThresholds(vitals, route);
  }

  /**
   * Check performance against thresholds
   */
  checkPerformanceThresholds(vitals, route) {
    const warnings = [];

    if (vitals.lcp > this.thresholds.lcp) {
      warnings.push(
        `LCP: ${vitals.lcp.toFixed(0)}ms (threshold: ${this.thresholds.lcp}ms)`,
      );
    }
    if (vitals.fid > this.thresholds.fid) {
      warnings.push(
        `FID: ${vitals.fid.toFixed(0)}ms (threshold: ${this.thresholds.fid}ms)`,
      );
    }
    if (vitals.cls > this.thresholds.cls) {
      warnings.push(
        `CLS: ${vitals.cls.toFixed(3)} (threshold: ${this.thresholds.cls})`,
      );
    }
    if (vitals.ttfb > this.thresholds.ttfb) {
      warnings.push(
        `TTFB: ${vitals.ttfb.toFixed(0)}ms (threshold: ${this.thresholds.ttfb}ms)`,
      );
    }

    if (warnings.length > 0 && import.meta.env.MODE === "development") {
      console.warn(`⚠️ Performance warnings for ${route}:`, warnings);
    }
  }

  /**
   * Set up bundle size tracking
   */
  setupBundleTracking() {
    // Bundle metrics will be populated by resource observer
    this.bundleLoadTimes = new Map();
  }

  /**
   * Track bundle loading performance
   */
  trackBundleLoad(entry) {
    const bundleName = this.extractBundleName(entry.name);
    const metrics = {
      name: bundleName,
      url: entry.name,
      size: entry.transferSize || entry.encodedBodySize || 0,
      duration: entry.duration,
      loadTime: entry.responseEnd - entry.startTime,
      timestamp: Date.now(),
    };

    this.metrics.bundleMetrics.set(bundleName, metrics);

    // Check bundle size threshold
    if (metrics.size > this.thresholds.bundleSize) {
      console.warn(
        `⚠️ Large bundle detected: ${bundleName} (${(metrics.size / 1024).toFixed(0)}KB)`,
      );
    }
  }

  /**
   * Extract bundle name from URL
   */
  extractBundleName(url) {
    const match = url.match(/\/([^/]+)-[a-f0-9]+\.js$/);
    return match ? match[1] : "unknown";
  }

  /**
   * Get enhanced performance report
   */
  getEnhancedPerformanceReport() {
    const report = {
      routes: {},
      bundles: {},
      memory: {},
      webVitals: {},
      summary: {},
    };

    // Route metrics
    this.metrics.routeMetrics.forEach((metrics, route) => {
      const latestVitals =
        metrics.webVitals[metrics.webVitals.length - 1] || {};
      report.routes[route] = {
        visits: metrics.visits,
        avgDuration: `${metrics.avgDuration.toFixed(0)}ms`,
        latestWebVitals: latestVitals,
      };
    });

    // Bundle metrics
    let totalBundleSize = 0;
    this.metrics.bundleMetrics.forEach((metrics, bundle) => {
      totalBundleSize += metrics.size;
      report.bundles[bundle] = {
        size: `${(metrics.size / 1024).toFixed(1)}KB`,
        loadTime: `${metrics.loadTime.toFixed(0)}ms`,
      };
    });
    report.bundles.totalSize = `${(totalBundleSize / 1024).toFixed(1)}KB`;

    // Memory metrics
    const memoryUsage = this.getCurrentMemoryUsage();
    if (memoryUsage) {
      report.memory = memoryUsage;
    }

    // Web Vitals
    report.webVitals = {
      lcp: getLCP(),
      fid: getFID(),
      cls: getCLS(),
      ttfb: getTTFB(),
    };

    // Calculate overall score
    report.summary = this.calculatePerformanceScore();

    return report;
  }

  /**
   * Calculate overall performance score
   */
  calculatePerformanceScore() {
    const scores = {
      lcp: 0,
      fid: 0,
      cls: 0,
      ttfb: 0,
      overall: 0,
    };

    // Get latest web vitals
    const vitals = {
      lcp: getLCP(),
      fid: getFID(),
      cls: getCLS(),
      ttfb: getTTFB(),
    };

    // Score based on thresholds (0-100)
    scores.lcp = Math.max(0, 100 - (vitals.lcp / this.thresholds.lcp) * 50);
    scores.fid = Math.max(0, 100 - (vitals.fid / this.thresholds.fid) * 50);
    scores.cls = Math.max(0, 100 - (vitals.cls / this.thresholds.cls) * 50);
    scores.ttfb = Math.max(0, 100 - (vitals.ttfb / this.thresholds.ttfb) * 50);

    scores.overall = (scores.lcp + scores.fid + scores.cls + scores.ttfb) / 4;

    return scores;
  }

  /**
   * Create developer tools
   */
  createDeveloperTools() {
    // // Performance dashboard button
    // const dashboardBtn = document.createElement('button');
    // dashboardBtn.innerHTML = '📈 Perf Dashboard';
    // dashboardBtn.style.cssText = `
    //   position: fixed;
    //   bottom: 10px;
    //   left: 240px;
    //   z-index: 9999;
    //   background: #9333ea;
    //   color: white;
    //   border: none;
    //   border-radius: 4px;
    //   padding: 8px 12px;
    //   font-family: monospace;
    //   cursor: pointer;
    //   font-size: 12px;
    // `;
    // dashboardBtn.addEventListener('click', () => {
    //   this.showPerformanceDashboard();
    // });
    // document.body.appendChild(dashboardBtn);
  }

  /**
   * Show performance dashboard
   */
  showPerformanceDashboard() {
    const report = this.getEnhancedPerformanceReport();

    console.group("🚀 NexusDialer Performance Dashboard");

    // Overall Score
    console.log(
      "%cPerformance Score",
      "font-size: 18px; font-weight: bold; color: #9333ea",
    );
    console.table(report.summary);

    // Web Vitals
    console.log(
      "%cCore Web Vitals",
      "font-size: 16px; font-weight: bold; color: #3182ce",
    );
    console.table({
      "LCP (Largest Contentful Paint)": `${report.webVitals.lcp.toFixed(0)}ms`,
      "FID (First Input Delay)": `${report.webVitals.fid.toFixed(0)}ms`,
      "CLS (Cumulative Layout Shift)": report.webVitals.cls.toFixed(3),
      "TTFB (Time To First Byte)": `${report.webVitals.ttfb.toFixed(0)}ms`,
    });

    // Route Performance
    console.log(
      "%cRoute Performance",
      "font-size: 16px; font-weight: bold; color: #10b981",
    );
    console.table(report.routes);

    // Bundle Analysis
    console.log(
      "%cBundle Analysis",
      "font-size: 16px; font-weight: bold; color: #f59e0b",
    );
    console.table(report.bundles);

    // Memory Usage
    console.log(
      "%cMemory Usage",
      "font-size: 16px; font-weight: bold; color: #ef4444",
    );
    console.table(report.memory);

    console.groupEnd();
  }
}

export const usePerformance = (componentName) =>
  useComponentPerformance(componentName);

// Global performance service instance
const performanceService = new PerformanceService();


export default performanceService;
